import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../services/api';
import { ArrowLeft, Save, Loader2, Plus, Trash2 } from 'lucide-react';

interface Client { id: string; name: string; default_currency: string; }

interface RecurringItem {
  description: string;
  quantity: number;
  unit_price: string;
  tax_rate: string;
}

interface RecurringData {
  client_id: string;
  frequency: string;
  start_date: string;
  end_date: string;
  next_invoice_date: string;
  currency: string;
  notes: string;
  terms: string;
  is_active: boolean;
  items: RecurringItem[];
}

const emptyItem: RecurringItem = { description: '', quantity: 1, unit_price: '0', tax_rate: '15' };

export default function RecurringForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [clients, setClients] = useState<Client[]>([]);
  const [formData, setFormData] = useState<RecurringData>({
    client_id: '',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    next_invoice_date: new Date().toISOString().split('T')[0],
    currency: 'ZAR',
    notes: '',
    terms: '',
    is_active: true,
    items: [{ ...emptyItem }],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClients();
    if (isEditing) fetchRecurring();
  }, [id]);

  const fetchClients = async () => {
    try {
      const data = await apiGet<{ items: Client[] }>('/api/clients?page_size=100');
      setClients(data.items);
    } catch (err) { console.error(err); }
  };

  const fetchRecurring = async () => {
    setIsLoading(true);
    try {
      const data = await apiGet<any>(`/api/recurring/${id}`);
      setFormData({
        client_id: data.client_id || '',
        frequency: data.frequency || 'monthly',
        start_date: data.start_date?.split('T')[0] || '',
        end_date: data.end_date?.split('T')[0] || '',
        next_invoice_date: data.next_invoice_date?.split('T')[0] || '',
        currency: data.currency || 'ZAR',
        notes: data.notes || '',
        terms: data.terms || '',
        is_active: data.is_active ?? true,
        items: data.items?.length ? data.items.map((item: any) => ({
          description: item.description || '',
          quantity: item.quantity || 1,
          unit_price: item.unit_price?.toString() || '0',
          tax_rate: item.tax_rate?.toString() || '15',
        })) : [{ ...emptyItem }],
      });
    } catch (err) {
      setError('Failed to load recurring invoice');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.client_id) { setError('Please select a client'); return; }

    setIsSaving(true);
    setError(null);

    try {
      const payload = {
        client_id: formData.client_id,
        frequency: formData.frequency,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        next_invoice_date: formData.next_invoice_date,
        currency: formData.currency,
        notes: formData.notes || null,
        terms: formData.terms || null,
        is_active: formData.is_active,
        items: formData.items.filter(item => item.description).map(item => ({
          description: item.description,
          quantity: Number(item.quantity),
          unit_price: item.unit_price,
          tax_rate: item.tax_rate,
        })),
      };

      if (isEditing) {
        await apiPut(`/api/recurring/${id}`, payload);
      } else {
        await apiPost('/api/recurring', payload);
      }
      navigate('/admin/recurring');
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const addItem = () => setFormData(prev => ({ ...prev, items: [...prev.items, { ...emptyItem }] }));
  const removeItem = (index: number) => setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  const updateItem = (index: number, field: keyof RecurringItem, value: string | number) => {
    setFormData(prev => ({ ...prev, items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item) }));
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-primary-500 animate-spin" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <button onClick={() => navigate('/admin/recurring')} className="flex items-center gap-2 text-gray-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Back to Recurring
        </button>
      </div>

      <div className="bg-dark-900 rounded-xl border border-dark-800 p-6">
        <h1 className="text-2xl font-bold text-white mb-6">{isEditing ? 'Edit Recurring Invoice' : 'New Recurring Invoice'}</h1>

        {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Client *</label>
              <select value={formData.client_id} onChange={(e) => setFormData(prev => ({ ...prev, client_id: e.target.value }))} required className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">Select client</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Frequency *</label>
              <select value={formData.frequency} onChange={(e) => setFormData(prev => ({ ...prev, frequency: e.target.value }))} className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Currency</label>
              <select value={formData.currency} onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))} className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="ZAR">ZAR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Start Date *</label>
              <input type="date" value={formData.start_date} onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))} required className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">End Date</label>
              <input type="date" value={formData.end_date} onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))} className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Next Invoice Date *</label>
              <input type="date" value={formData.next_invoice_date} onChange={(e) => setFormData(prev => ({ ...prev, next_invoice_date: e.target.value }))} required className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          {/* Line Items */}
          <div>
            <h3 className="text-lg font-medium text-white mb-3">Line Items</h3>
            <div className="space-y-3">
              {formData.items.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-5">
                    <input type="text" placeholder="Description" value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-sm" />
                  </div>
                  <div className="col-span-2">
                    <input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} min="0.01" step="any" className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-sm" />
                  </div>
                  <div className="col-span-2">
                    <input type="number" placeholder="Price" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', e.target.value)} step="0.01" className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-sm" />
                  </div>
                  <div className="col-span-2">
                    <input type="number" placeholder="Tax %" value={item.tax_rate} onChange={(e) => updateItem(index, 'tax_rate', e.target.value)} step="0.01" className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-sm" />
                  </div>
                  <div className="col-span-1">
                    <button type="button" onClick={() => removeItem(index)} className="p-2 text-red-400 hover:bg-dark-700 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" onClick={addItem} className="mt-3 flex items-center gap-2 text-sm text-primary-500 hover:text-primary-400">
              <Plus className="w-4 h-4" /> Add Line Item
            </button>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))} className="w-4 h-4 rounded border-dark-700 bg-dark-800 text-primary-500" />
            <label htmlFor="is_active" className="text-sm text-gray-300">Active</label>
          </div>

          <div className="flex gap-4 pt-4">
            <button type="submit" disabled={isSaving} className="flex items-center gap-2 px-6 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg">
              {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={() => navigate('/admin/recurring')} className="px-6 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
